import { createSlice } from "@reduxjs/toolkit";
import {
  clearAllAuthPersistence,
  persistAccessToken,
  readPersistedAccessToken,
} from "../../utils/storage";
import { AUTH_LS_KEYS } from "../../constants/authStorage";

/**
 * JSON-shaped auth fields in localStorage (written via JSON.stringify).
 * Returns { value, corrupt }. Empty/missing is not corrupt; invalid JSON is.
 */
function readAuthJson(key) {
  if (typeof localStorage === "undefined") {
    return { value: null, corrupt: false };
  }
  const raw = localStorage.getItem(key);
  if (raw == null || raw.trim() === "") {
    return { value: null, corrupt: false };
  }
  try {
    return { value: JSON.parse(raw), corrupt: false };
  } catch {
    return { value: null, corrupt: true };
  }
}

function getInitialAuthState() {
  const loggedOut = {
    coopData: null,
    companyId: null,
    userData: null,
    token: null,
    user_type: null,
    enums: undefined,
  };

  const coop = readAuthJson(AUTH_LS_KEYS.COOP_PROFILE);
  const companyId = readAuthJson(AUTH_LS_KEYS.COMPANY_ID);
  const userData = readAuthJson(AUTH_LS_KEYS.USER_PROFILE);
  const user_type = readAuthJson(AUTH_LS_KEYS.USER_TYPE);
  const enums = readAuthJson(AUTH_LS_KEYS.ENUMS);

  if (
    coop.corrupt ||
    companyId.corrupt ||
    userData.corrupt ||
    user_type.corrupt ||
    enums.corrupt
  ) {
    clearAllAuthPersistence();
    return loggedOut;
  }

  const token = readPersistedAccessToken();

  return {
    coopData: coop.value || null,
    companyId: companyId.value || null,
    userData: userData.value || null,
    token,
    user_type: user_type.value || null,
    enums: enums.value,
  };
}

export const authSlice = createSlice({
  name: "auth",
  initialState: getInitialAuthState(),
  reducers: {
    setCoopData: (state, { payload }) => {
      state.coopData = payload;
      storageLocalSet(AUTH_LS_KEYS.COOP_PROFILE, payload);
    },
    setCompanyId: (state, { payload }) => {
      state.companyId = payload;
      storageLocalSet(AUTH_LS_KEYS.COMPANY_ID, payload);
    },
    setToken: (state, { payload }) => {
      state.token = payload;
      persistAccessToken(payload);
    },
    setUserType: (state, { payload }) => {
      state.user_type = payload;
      storageLocalSet(AUTH_LS_KEYS.USER_TYPE, payload);
    },
    setUserData: (state, { payload }) => {
      state.userData = payload;
      storageLocalSet(AUTH_LS_KEYS.USER_PROFILE, payload);
    },
    setEnums: (state, { payload }) => {
      state.enums = payload;
      storageLocalSet(AUTH_LS_KEYS.ENUMS, payload);
    },
    logout: (state) => {
      state.coopData = null;
      state.userData = null;
      state.companyId = null;
      state.token = null;
      state.user_type = null;
      state.enums = undefined;
      clearAllAuthPersistence();
    },
  },
});

function storageLocalSet(key, value) {
  try {
    localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

export const { reducer, actions } = authSlice;
export const {
  logout,
  setCoopData,
  setUserData,
  setToken,
  setUserType,
  setCompanyId,
  setEnums,
} = actions;
export const authState = reducer;
export const selectCurrentCoopData = (state) => state.auth.coopData;
export const selectCurrentUserData = (state) => state.auth.userData;
export const selectCurrentCompanyId = (state) => state.auth.companyId;
export const selectToken = (state) => state.auth.token;
export const selectUserType = (state) => state.auth.user_type;
export const selectEnums = (state) => state.auth.enums;
